import React from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import "chart.js/auto";
import { projectNameOf, isProjectRunning } from "../Common/ProjectsTable";
import "./ProjectCharts.scss";

/* The same ramp the dashboard charts use. */
const BLUE = "#2b8fe0";
const BLUE_D = "#1c4f86";
const GREEN = "#0f9d63";
const SLATE = "#94a3b8";
const LINE = "#eef2f7";
const INK = "#334155";

const FONT = { family: "Figtree, sans-serif", size: 12 };

/* Everything here is counted from the list the API already returned — no
   second request, and nothing invented. */
const ProjectCharts = ({ projects = [] }) => {

    if (!projects.length) return null;

    const running = projects.filter(isProjectRunning).length;
    const completed = projects.length - running;

    const statusData = {
        labels: ["Running", "Completed"],
        datasets: [{
            data: [running, completed],
            backgroundColor: [GREEN, SLATE],
            borderColor: "#ffffff",
            borderWidth: 3,
            hoverOffset: 6,
        }],
    };

    /* the eight biggest teams, largest first */
    const bySize = [...projects]
        .map((p) => ({
            name: projectNameOf(p),
            size: (p?.employeeList || []).filter((e) => e && e.employeeName).length,
        }))
        .sort((a, b) => b.size - a.size)
        .slice(0, 8);

    const teamData = {
        labels: bySize.map((p) => p.name),
        datasets: [{
            label: "People",
            data: bySize.map((p) => p.size),
            backgroundColor: bySize.map((_, i) =>
                i === 0 ? BLUE_D : BLUE),
            borderRadius: 8,
            barThickness: 18,
            maxBarThickness: 22,
        }],
    };

    const totalPeople = projects.reduce(
        (t, p) => t + (p?.employeeList || []).filter((e) => e && e.employeeName).length, 0);

    return (
        <div className="pr-charts">

            <div className="pr-chart">
                <div className="pr-chart-head">
                    <h6>Project status</h6>
                    <span>{projects.length} projects</span>
                </div>

                <div className="pr-chart-body pr-chart-body--ring">
                    <Doughnut
                        data={statusData}
                        options={{
                            cutout: "64%",
                            maintainAspectRatio: false,
                            animation: { duration: 700, easing: "easeOutQuart" },
                            plugins: {
                                legend: {
                                    position: "bottom",
                                    labels: {
                                        font: FONT, color: INK, usePointStyle: true,
                                        pointStyle: "circle", padding: 16, boxWidth: 8,
                                    },
                                },
                                tooltip: {
                                    backgroundColor: "#1e293b",
                                    padding: 10,
                                    cornerRadius: 10,
                                    titleFont: FONT,
                                    bodyFont: FONT,
                                    displayColors: false,
                                },
                            },
                        }}
                    />
                    <div className="pr-ring-total">
                        <b>{running}</b>
                        <span>running</span>
                    </div>
                </div>
            </div>

            <div className="pr-chart pr-chart--wide">
                <div className="pr-chart-head">
                    <h6>Team size by project</h6>
                    <span>{totalPeople} people assigned</span>
                </div>

                <div className="pr-chart-body">
                    <Bar
                        data={teamData}
                        options={{
                            indexAxis: "y",
                            maintainAspectRatio: false,
                            animation: { duration: 700, easing: "easeOutQuart" },
                            plugins: {
                                legend: { display: false },
                                tooltip: {
                                    backgroundColor: "#1e293b",
                                    padding: 10,
                                    cornerRadius: 10,
                                    titleFont: FONT,
                                    bodyFont: FONT,
                                    displayColors: false,
                                    callbacks: {
                                        label: (c) => `${c.parsed.x} ${c.parsed.x === 1 ? "person" : "people"}`,
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    beginAtZero: true,
                                    ticks: { precision: 0, font: FONT, color: SLATE },
                                    grid: { color: LINE, drawBorder: false },
                                },
                                y: {
                                    ticks: {
                                        font: FONT, color: INK,
                                        /* a long project name would otherwise
                                           squeeze the plot to nothing */
                                        callback(value) {
                                            const label = this.getLabelForValue(value);
                                            return label.length > 22 ? `${label.slice(0, 21)}…` : label;
                                        },
                                    },
                                    grid: { display: false, drawBorder: false },
                                },
                            },
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProjectCharts;
